import { getAuth } from 'firebase/auth';
import { useCallback, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useServiceMode } from 'src/contexts/ServiceModeContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { getPayloadJWT, isJWT } from 'src/utils/jwt';

export const OrganizationLockGuard = () => {
  const auth = getAuth();
  const { get } = useAuthedRequest();
  const { serviceKey } = useServiceMode();
  const navigate = useNavigate();

  const fetchCurrentUserAndOrganization = useCallback(async () => {
    const token = await auth.currentUser?.getIdToken();

    if (token && isJWT(token) && !serviceKey) {
      const decoded = getPayloadJWT(token);

      // Organization access is validated by OrganizationAccessGuard,
      // so we can trust that decoded.organization is valid at this point
      if (decoded.organization) {
        try {
          const organization = await get(
            `/organizations/${decoded.organization.id}`,
          );
          if (
            organization?.lockAt &&
            new Date(organization.lockAt).getTime() < new Date().getTime()
          ) {
            navigate('/billing?error=plan-expired');
          }
        } catch (error) {
          // Organization access is validated earlier, so unexpected errors here
          // indicate a problem that should be logged for debugging
          console.error(
            `Failed to fetch organization ${decoded.organization.id}`,
            error,
          );
        }
      }
    }
  }, [auth, get, navigate, serviceKey]);

  useEffect(() => {
    if (!auth) {
      return;
    }

    fetchCurrentUserAndOrganization();
  }, [auth, fetchCurrentUserAndOrganization]);

  return <Outlet />;
};
